import { api, unwrap, type ApiEnvelope } from '@/lib/api';

// Client des vues métier cloisonnées : un seul endpoint par vue, protégé côté
// serveur par la permission du rôle (finance/logistique/support/direction).
// Remplace la recomposition depuis 7 endpoints de module non cloisonnés (3.2/3.3).

export interface BizMeta {
  generatedAt: string;
  period: { from: string; to: string };
}

export interface BizSales {
  orders: number;
  revenue: number;
  averageBasket: number;
}

export interface BizFunnel {
  created: number;
  paid: number;
  shipped: number;
  delivered: number;
  completed: number;
  cancelled: number;
  conversionRate: number;
  orderCompletionRate: number;
}

export interface BizRevenuePoint {
  date: string;
  revenue: number;
  orders: number;
}

export interface BizReconciliation {
  status: 'OK' | 'WARN';
  orderRevenue: number;
  invoicedRevenue: number;
  accountingRevenue: number;
  differences: {
    ordersVsInvoices: number;
    invoicesVsAccounting: number;
    ordersVsAccounting: number;
  };
}

export interface BizFinance {
  treasury: number;
  receivables: number;
  payables: number;
  overdueCount: number;
  overdueAmount: number;
  vatCollectedMonth: number;
  alerts: Array<{ level: 'info' | 'warn' | 'danger'; message: string }>;
}

export interface BizTreasuryAccount {
  accountCode: string;
  label: string;
  balance: number;
}

export interface BizLogistics {
  shipments: { total: number; delivered: number };
  slaSuccessRate: number;
  deliveryRate: number;
  firstAttemptSuccessRate: number;
}

export interface BizLateShipment {
  id: string;
  code: string;
  status: string;
  codAmount: number | null;
  slaDueAt: string | null;
}

export interface BizActiveTour {
  id: string;
  code: string;
  driverName: string | null;
  totalStops: number;
  plate: string | null;
}

export interface BizSupport {
  total: number;
  open: number;
  pending: number;
  urgent: number;
  resolved: number;
  unassigned: number;
  slaBreached: number;
}

export interface BizUrgentTicket {
  id: string;
  number: string;
  subject: string;
  priority: string;
  status: string;
  slaDueAt: string | null;
}

export interface BizCustomer {
  acquired: number;
  activation30dRate: number;
  retention90dRate: number;
  acquisition: { total: number; bySource: Array<{ source: string; count: number }> };
}

export interface BizCatalog {
  published: number;
  draft: number;
  paused: number;
  archived: number;
  total: number;
}

export interface DirectionReport extends BizMeta {
  sales: BizSales;
  funnel: BizFunnel;
  revenueSeries: BizRevenuePoint[];
  reconciliation: BizReconciliation;
  today: { revenue: number; orders: number; slaLate: number };
  finance: BizFinance;
  customer: BizCustomer;
  logistics: BizLogistics;
  support: BizSupport;
  catalog: BizCatalog;
}

export interface FinanceReport extends BizMeta {
  sales: BizSales;
  reconciliation: BizReconciliation;
  finance: BizFinance & { treasuryByAccount: BizTreasuryAccount[] };
}

export interface LogisticsReport extends BizMeta {
  logistics: BizLogistics;
  lateShipments: { items: BizLateShipment[]; total: number };
  tours: BizActiveTour[];
}

export interface SupportReport extends BizMeta {
  support: BizSupport;
  urgentTickets: { items: BizUrgentTicket[]; total: number };
}

export interface BusinessReportByRole {
  direction: DirectionReport;
  finance: FinanceReport;
  logistics: LogisticsReport;
  support: SupportReport;
}

export const reportingBusinessApi = {
  get: <R extends keyof BusinessReportByRole>(role: R): Promise<BusinessReportByRole[R]> =>
    unwrap<BusinessReportByRole[R]>(
      api.get<ApiEnvelope<BusinessReportByRole[R]>>(`/admin/reporting/business/${role}`),
    ),
};
